import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import { v4 as uuid } from 'uuid';
import * as trustStartService from '../../services/truststartService';
import DealerIBVLandingPageView from './DealerIBVLandingPage.view';
import appSyncClient from '../../appsync/client';
import createUser from '../../appsync/mutations/CreateUser';

const initialFormData = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  loanReferenceId: '',
};

const getStep = (stepString) => {
  if (!stepString) return 0;
  return parseInt(stepString, 10);
};

const getDefaultIsRequestCreated = ({ location }) => (
  location
  && location.state
  && location.state.isRequestCreated
);

const getInitialFormState = (locationState, loanReferenceId = '') => {
  let formData = initialFormData;
  if (locationState && locationState.formData) {
    ({ formData } = locationState);
  }
  return { ...formData, loanReferenceId };
};

export default function DealerIBVLandingPageContainer(props) {
  const { clientId } = props.match.params;
  const queryStringParams = queryString.parse(props.location.search);
  const { id: loanReferenceId } = queryStringParams;
  const [requestId, setRequestId] = useState(uuid());
  const [config, setConfig] = useState({ loading: true });
  const [validations, setValidation] = useState({});
  const [formData, setFormData] = useState(getInitialFormState(
    props.location.state,
    loanReferenceId,
  ));
  const [isRequestCreated, setIsRequestCreated] = useState(getDefaultIsRequestCreated(props));
  const [errorState, setErrorState] = useState(false);

  const step = getStep(queryStringParams.step);
  const nextStep = (history, state) => {
    const qs = queryString.stringify({
      id: loanReferenceId,
      step: step + 1,
    });
    const { agreements } = state || {};
    const {
      firstName,
      lastName,
      phone,
      email,
    } = formData || {};
    const url = `${props.location.pathname}?${qs}`;
    const data = {
      bankingConfig: config.banking,
      scoringRequest: {
        clientId,
        requestId,
        isCreated: isRequestCreated,
      },
      formData: {
        firstName,
        lastName,
        phone,
        email,
      },
      agreements,
    };
    history.push(url, data);
  };

  const onAgreementAccepted = async (agreements) => {
    try {
      await appSyncClient
        .mutate({
          mutation: createUser(
            formData.email,
            !!agreements?.allowResearch,
            agreements?.dateAgreedToMobileTerms,
          ),
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to create user with agreement:', error);
    }
    nextStep(props.history, { agreements });
  };

  useEffect(() => {
    async function fetchConfig() {
      const result = await trustStartService.getPublicClientConfig(clientId);
      setConfig(result);
    }

    fetchConfig();
  }, [clientId]);

  useEffect(() => {
    async function findScoringRequest() {
      const result = await trustStartService.findScoringRequest({ clientId, loanReferenceId });
      if (result.found) {
        setRequestId(result.requestId);
      }
    }
    findScoringRequest();
  }, [clientId, loanReferenceId]);

  useEffect(() => {
    const useExistingRequestId = (
      props.location.state
      && props.location.state.scoringRequest
      && props.location.state.scoringRequest.requestId
    );
    if (useExistingRequestId) {
      setRequestId(useExistingRequestId);
    }
  }, [props.location.state]);

  const onFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const enterErrorState = () => {
    setErrorState(true);
    return { clientId, requestId, error: true };
  };

  const createScoringRequest = async () => {
    if (isRequestCreated) return Promise.resolve({ clientId, requestId });
    try {
      const result = await trustStartService.dealerIbvRequest({
        clientId,
        requestId,
        agreements: props.location.state.agreements,
        ...formData,
      });
      if (!result || /error/i.test(result.message)) {
        return enterErrorState();
      }
      setIsRequestCreated(true);
      return { clientId, requestId, ...result };
    } catch (error) {
      return enterErrorState();
    }
  };

  return (
    <DealerIBVLandingPageView
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      clientId={clientId}
      step={step}
      nextStep={nextStep}
      loanReferenceId={loanReferenceId}
      validations={validations}
      formData={formData}
      onFieldChange={onFieldChange}
      setValidation={setValidation}
      config={config}
      createScoringRequest={createScoringRequest}
      errorState={errorState}
      onError={enterErrorState}
      onAgreementAccepted={onAgreementAccepted}
    />
  );
}
